/**
 * useAnalyticsTierSync Hook
 *
 * Pushes the current subscription tier (`personal | pro | trial`) to the main
 * process analytics context whenever it changes, so every Umami event carries
 * an up-to-date `subscription` tag.
 *
 * Without this, the tag is stamped once at startup from the main-process
 * license cache and never refreshed — which meant `trial` was never emitted
 * (trial state lives only in the renderer) and `pro` was badly undercounted
 * (users upgrading mid-session stayed tagged `personal`).
 *
 * Mirrors the state sources of `useHasProAccess`, but keeps trial and paid Pro
 * distinct via `deriveSubscriptionTier`. Modeled on `useEntitlementSync`.
 *
 * Mount once at the app root (App.tsx), alongside the other sync hooks.
 */

import { useEffect, useRef } from 'react';
import { useLicenseStore } from '../store/licenseStore';
import {
  useSubscriptionStore,
  selectIsPro,
  selectIsInGracePeriod,
} from '../store/subscriptionStore';
import { useAuthStore } from '../store/authStore';
import { getTrialState } from '../services/trialService';
import { deriveSubscriptionTier } from '../../analytics/deriveSubscriptionTier';
import type { SubscriptionType } from '../../analytics/analyticsTypes';

/** Dev/test-only Pro build (never true in production). Mirrors useHasProAccess. */
function isProBuild(): boolean {
  if (!import.meta.env.DEV) return false;
  return import.meta.env.VITE_PRO_MODE === 'true';
}

export function useAnalyticsTierSync(): void {
  const licenseState = useLicenseStore((s) => s.state);
  const licenseMode = useLicenseStore((s) => s.state.mode);
  const isInitialized = useLicenseStore((s) => s.isInitialized);
  const userId = useAuthStore((s) => s.user?.id);
  const isSubscriptionPro = useSubscriptionStore(selectIsPro);
  const isInGracePeriod = useSubscriptionStore(selectIsInGracePeriod);

  const lastSentRef = useRef<SubscriptionType | null>(null);

  useEffect(() => {
    // Wait until license state has loaded so the first push isn't a wrong guess.
    if (!isInitialized) return;

    const setContext = window.electronAPI?.analytics?.setContext;
    if (!setContext) return;

    const trialActive = getTrialState(licenseState, userId) === 'active';
    const isDevPro = isProBuild() && licenseMode === 'pro';

    const tier = deriveSubscriptionTier({
      isSubscriptionPro,
      isInGracePeriod,
      isDevPro,
      trialActive,
    });

    if (lastSentRef.current === tier) return;
    lastSentRef.current = tier;

    // Fire-and-forget; analytics must never affect UX.
    void setContext({ subscription: tier }).catch(() => {});
  }, [licenseState, licenseMode, isInitialized, userId, isSubscriptionPro, isInGracePeriod]);
}

export default useAnalyticsTierSync;
